import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Box, Typography, Drawer, Collapse, useMediaQuery, useTheme, IconButton } from '@mui/material'

import SearchIcon from '../../../assets/search.svg'
import { type IDistributionSeries } from '../../../models/DistributionSeries'
import { ProjectActions } from './ProjectActions'
import Search from '../../../components/Search'
import { blue, grey, shade } from '../../../colors'
import { capitalizeFirst } from '../../../utils/utils'
import { ImageWrapper } from '../../../components/CustomTable/NoDataFound/styles'
import { type ProjectScopeEnum } from '../../../pages/ProjectPage'
import CloseIcon from '@mui/icons-material/Close'

interface ProjectsSidebarProps {
  projects: IDistributionSeries[]
  permissions?: string[]
  scope: ProjectScopeEnum
  onScopeChange: (scope: ProjectScopeEnum) => void
  children?: React.ReactNode // for main content (table)
  showProjectSidebar: boolean
  setShowProjectSidebar: (arg: boolean) => void
}

export const ProjectsSidebar: React.FC<ProjectsSidebarProps> = ({ projects, permissions, scope, onScopeChange, children, showProjectSidebar, setShowProjectSidebar }) => {
  const [search, setSearch] = useState('')
  const [hoverAnchor, setHoverAnchor] = useState<string>('')
  const [searchParams, setSearchParams] = useSearchParams()
  const sortedDefaultValue = projects[0]?.distributionSeriesId || ''
  const showActions = permissions?.includes('DELETE') || permissions?.includes('EDIT')
  const theme = useTheme()
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'))

  const selectedProjectId = searchParams.get('projectId') || sortedDefaultValue

  // • Normalizes search (trim + lowercase) for consistent matching
  // • If search is empty, return the full list (no filtering)
  const filteredProjects = useMemo(() => {
    if (!Array.isArray(projects)) return []
    const trimmedSearch = search.trim().toLowerCase()
    return trimmedSearch
      ? projects.filter((series) => series?.title?.toLowerCase().includes(trimmedSearch))
      : projects
  }, [projects, search])

  // • Clicking a row updates the URL query param (keeps selection shareable/bookmarkable)
  // • Uses replace:true to avoid pushing a new history entry for every click
  const handleSidebarRowClick = (_e: React.MouseEvent, projectId: string): void => {
    const params = new URLSearchParams(searchParams)
    params.set('projectId', projectId)
    setSearchParams(params, { replace: true })
  }

  // • Ensures there’s always a valid projectId in the URL
  // • If missing, auto-select the default (first project) and write it to query params
  useEffect(() => {
    const currentProjectId = searchParams.get('projectId')

    const params = new URLSearchParams(searchParams)

    if (!currentProjectId && sortedDefaultValue) {
      params.set('projectId', sortedDefaultValue)
      setSearchParams(params, { replace: true })
    }
  }, [projects, searchParams, setSearchParams, sortedDefaultValue])

  const hasProjectsToShow = filteredProjects && filteredProjects.length > 0

  const sidebarContent = (
    <Box sx={{ position: 'relative', height: '80vh', p: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box sx={{ position: 'sticky', top: 0, zIndex: 2, pb: 2, background: '#fff' }}>
        <Search
          placeholder="Search projects..."
          onChange={setSearch}
          onSearch={setSearch}
          fillContainer
          stayFocusOnEnter
        />
      </Box>
      <Box
        pr={3}
        mt={3}
        sx={{
          overflowY: 'auto',
          borderRight: `1px solid ${grey[100]}`,
          flexGrow: 1
        }}
      >
        {!hasProjectsToShow && <NoProjectsFoundMessage search={search} />}
        {hasProjectsToShow &&
          filteredProjects?.map((series) => {
            // • Computes active state by comparing current row id vs URL-selected id
            const isActive = String(series.distributionSeriesId) === selectedProjectId
            return (
              <Box
                key={series.distributionSeriesId}
                padding={2}
                display='flex'
                alignItems='center'
                justifyContent={'space-between'}
                borderBottom={`1px solid ${grey[0]}`}
                onMouseEnter={() => { setHoverAnchor(series.distributionSeriesId) }}
                onMouseLeave={() => { setHoverAnchor('') }}
                sx={{
                  cursor: 'pointer',
                  borderRadius: '6px',
                  background: isActive ? shade[950] : 'transparent'
                }}
                onClick={e => { handleSidebarRowClick(e, series.distributionSeriesId) }}
                aria-current={isActive ? 'page' : undefined}
                tabIndex={0}
                role="button"
              >
                <Typography
                  title={series.title}
                  color={isActive ? blue[500] : grey[850]}
                  fontSize={14}
                  sx={{ width: '85%' }}
                >
                  {capitalizeFirst(series.title)}
                </Typography>

                {/* • Show actions only when:
                    - user is hovering this row AND
                    - user has EDIT/DELETE permissions */}
                {(hoverAnchor === series.distributionSeriesId && showActions) && (
                  <ProjectActions selectedProject={series} scope={scope} />
                )}
              </Box>
            )
          })
        }
      </Box>
    </Box>
  )

  return (
    <Box display="flex" width="100%" height="60vh">
      {/* • Responsive behavior:
          - Medium screens: use Drawer (overlay) controlled by showProjectSidebar
          - Larger screens: use Collapse (inline) controlled by showProjectSidebar */}
      {isMediumScreen &&
        <Drawer
          anchor="left"
          open={showProjectSidebar}
          onClose={() => { setShowProjectSidebar(false) }}

          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
              maxWidth: '90vw',
              boxSizing: 'border-box',
              p: 2
            }
          }}
        >
          <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} pl={2}>
            <Typography fontWeight={'bold'}>{scope}</Typography>
            <IconButton
              aria-label="Close sidebar"
              onClick={() => { setShowProjectSidebar(false) }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ pt: 4 }}>
            {sidebarContent}
          </Box>
        </Drawer>
      }

      {!isMediumScreen &&
        <Collapse
          in={showProjectSidebar}
          orientation="horizontal"
          sx={{
            transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
          {sidebarContent}
        </Collapse>
      }
    </Box>
  )
}

interface NoProjectsFoundMessageProps {
  search?: string
  isMyView?: boolean
}

const NoProjectsFoundMessage: React.FC<NoProjectsFoundMessageProps> = ({ search, isMyView }) => {
  const hasSearch = Boolean(search?.trim())
  const isFilteredView = isMyView && !hasSearch
  return (
    <Box display={'flex'} flexDirection='column' alignItems='center' justifyContent={'center'} gap={2} height={'60vh'} >
      {hasSearch
        ? <>
          <ImageWrapper>
            <img src={SearchIcon} alt="Search Icon" />
          </ImageWrapper>
          <Box>
            <Typography fontWeight='bold' textAlign='center'>No results</Typography>
            <Typography textAlign='center' color={grey[650]} fontSize='14px' lineHeight={2}>
              Please try again.
            </Typography>
          </Box>
        </>
        : (
          <Typography color={grey[500]} fontWeight="bold" fontSize={16} textAlign='center'>
            {isFilteredView
              ? 'No survey projects found in My View.'
              : <>No survey projects available.<br />Please create a new project to get started.</>}
          </Typography>
          )
      }
    </Box>
  )
}
